import { Link } from 'react-router-dom';
import { Badge, Card, Chip } from '@zarya/ui';
import { dataHour } from '@app/format';
import { t } from '@app/i18n';
import { delegationLabel } from '../../Contracts/ContractsModel';
import { useIdentityPolicies } from '../Identities.api';
import { TabState } from './TabState';
import s from '../Identities.module.css';

/**
 * Aba **Políticas** de T5 (FR-054, FR-088): as políticas extras que incluem esta
 * identidade, além das capabilities que ela traz do `agent.yaml`.
 *
 * Uma regra de Human Access alcança N identidades — o cadastro é em Access
 * Control › Human Access, e daqui só se lê e se abre a de lá.
 *
 * A regra autoriza dois tipos de concessão desde o DEF-29 (09/09): a tool, com o
 * nome dela, e a delegação (`kind: agent`), que não tem tool e traz o agente alvo
 * nos campos `target_*`. A delegação sai com o rótulo do contrato
 * (`delegationLabel`) e o selo de agente da aba Capabilities, e o «externo»
 * quando o alvo é de fora. Até o aim-903 este arquivo dizia que a regra não
 * concede A2A, e desenhava a delegação como uma tool sem nome: um badge vazio.
 *
 * A regra não amplia o que a identidade pode: quem concede é o contrato dela
 * (aba Capabilities). Uma concessão da regra fora do contrato não tem efeito.
 */

export interface PoliciesTabProps {
  identityId: string;
  ativa: boolean;
}

export function PoliciesTab({ identityId, ativa }: PoliciesTabProps) {
  const politicas = useIdentityPolicies(identityId, ativa);
  const itens = politicas.data ?? [];

  return (
    <TabState
      carregando={politicas.isLoading}
      erro={politicas.isError ? politicas.error : null}
      tituloErro={t('identities.policiesTab.error')}
      onRetry={() => void politicas.refetch()}
      rotulo={t('identities.policiesTab.loading')}
    >
      {itens.length === 0 ? (
        <p className={s.vazio}>{t('identities.policiesTab.empty')}</p>
      ) : (
        itens.map((politica) => (
          <Card
            key={politica.id}
            title={politica.name}
            subtitle={t('identities.policiesTab.updated', {
              when: dataHour(politica.updated_at) ?? '—',
              who: politica.updated_by ?? '—',
            })}
            className={s.cardEmpilhado}
            toolbar={
              <Link className={s.link} to={`/politicas?drawer=${politica.id}`}>
                {t('identities.policiesTab.open')}
              </Link>
            }
          >
            <span className={s.papel}>
              <Badge tone="neutral">{politica.mode === 'rego'
                  ? t('identities.policiesTab.mode.rego')
                  : t('identities.policiesTab.mode.ui')}</Badge>
              {politica.state === 'ativa' ? (
                <Badge tone="success">
                  {t('identities.policiesTab.state.active', { version: politica.version })}
                </Badge>
              ) : (
                <Badge tone="warning">{t('identities.policiesTab.state.draft')}</Badge>
              )}
            </span>

            {politica.description ? <p className={s.subTexto}>{politica.description}</p> : null}

            <div className={s.capLista} data-testid="rule-grants">
              {(politica.grants ?? []).map((concessao) =>
                concessao.kind === 'agent' ? (
                  <span
                    className={s.capItem}
                    key={`agent:${concessao.target_resource_id ?? concessao.target_path}`}
                    data-testid="rule-grant-agent"
                  >
                    <Chip icon="exchange">{delegationLabel(concessao)}</Chip>
                    {concessao.target_scope === 'external' ? (
                      <Badge tone="neutral">{t('identities.capabilitiesTab.external')}</Badge>
                    ) : null}
                  </span>
                ) : (
                  <Chip key={`tool:${concessao.tool_id}`}>{concessao.tool_name}</Chip>
                ),
              )}
              {(politica.grants ?? []).length === 0 ? (
                <span className={s.vazio}>{t('identities.policiesTab.noGrants')}</span>
              ) : null}
            </div>
          </Card>
        ))
      )}

      <p className={s.nota}>
        {t('identities.policiesTab.note')}{' '}
        <Link className={s.link} to="/politicas">
          {t('identities.policiesTab.seeAll')}
        </Link>
      </p>
    </TabState>
  );
}
